import {
  collection,
  deleteDoc,
  doc,
  type Firestore,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  type Timestamp,
  where,
} from 'firebase/firestore';

// Personal access tokens let outside tools (Phase 15) act for the user.
// The token is shown once; only its SHA-256 hash is stored, as the id of
// apiTokens/{hash}, so a leaked database does not leak tokens.
export const TOKEN_PREFIX = 'dinnr_';
export const MAX_NAME_LENGTH = 50;

export interface ApiToken {
  // The hash of the token
  id: string;
  name: string;
  createdAt?: number;
  // Set by the server each time the token is used
  lastUsedAt?: number;
}

export interface ApiTokenClientInterface {
  list(): Promise<ApiToken[]>;
  // Returns the token itself, the only time it is available
  create(name: string): Promise<{ token: string; apiToken: ApiToken }>;
  revoke(id: string): Promise<void>;
}

// 32 random bytes, base64url, after a recognisable prefix
export function generateToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const base64 = btoa(String.fromCharCode(...bytes));
  return (
    TOKEN_PREFIX +
    base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  );
}

// SHA-256 as 64 hex characters; the server hashes incoming tokens the same
// way
export async function hashToken(token: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(token),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function millis(value: unknown): number | undefined {
  return (value as Timestamp | null)?.toMillis?.();
}

export default class ApiTokenClient implements ApiTokenClientInterface {
  constructor(
    private readonly firestore: Firestore,
    private readonly uid: string,
  ) {}

  // Newest first
  async list(): Promise<ApiToken[]> {
    const snapshot = await getDocs(
      query(
        collection(this.firestore, 'apiTokens'),
        where('user', '==', this.uid),
      ),
    );
    return snapshot.docs
      .map((token) => {
        const data = token.data();
        return {
          id: token.id,
          name: String(data.name ?? ''),
          createdAt: millis(data.createdAt),
          lastUsedAt: millis(data.lastUsedAt),
        };
      })
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }

  async create(name: string): Promise<{ token: string; apiToken: ApiToken }> {
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > MAX_NAME_LENGTH) {
      throw new Error(
        `Give the token a name of at most ${MAX_NAME_LENGTH} characters.`,
      );
    }
    const token = generateToken();
    const id = await hashToken(token);
    await setDoc(doc(this.firestore, 'apiTokens', id), {
      user: this.uid,
      name: trimmed,
      createdAt: serverTimestamp(),
    });
    return { token, apiToken: { id, name: trimmed, createdAt: Date.now() } };
  }

  async revoke(id: string): Promise<void> {
    await deleteDoc(doc(this.firestore, 'apiTokens', id));
  }
}
